import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import { Dayjs } from "dayjs";
import { useAcademicYearContext } from "../../../context/AacademicYearContext";
import { fetchFacultyTimetable, fetchMyTimetable } from "../../academicOperations/services/timetableService";
import { FacultyTimetable } from "../../academicOperations/types";
import { applyLeaveForFaculty, applyMyLeave, leaveErrorMessage } from "../services/leaveService";
import { LEAVE_TYPES, LeaveHalfDay, LeaveRequest } from "../types";
import { daysLabel } from "../leaveFormat";
import {
  LEAVE_TEXT_MAX,
  LeaveForm,
  LeaveFormErrors,
  countDays,
  countPeriods,
  parseYearDate,
  validateLeave,
  workingDaysOf,
} from "../leaveValidation";

type Props = {
  open: boolean;
  onClose: () => void;
  onApplied: (leave: LeaveRequest) => void;
  /** "self": the logged-in faculty (PENDING). "admin": on a faculty's behalf (approved at once). */
  mode: "self" | "admin";
  /** admin: faculty to pick from */
  faculties?: { id: number; name: string }[];
  /** requests already made (self: own; admin: all faculty), for the overlap check */
  existing: LeaveRequest[];
};

const EMPTY: LeaveForm = { fromDate: null, toDate: null, halfDay: null, leaveType: "", reason: "" };
const toggleSx = { "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, fontSize: 13, px: 1.75, py: 0.5 } };

export const ApplyLeaveDialog: React.FC<Props> = ({ open, onClose, onApplied, mode, faculties = [], existing }) => {
  const { academicYears } = useAcademicYearContext();
  const [form, setForm] = useState<LeaveForm>(EMPTY);
  const [facultyId, setFacultyId] = useState<number | "">("");
  const [timetable, setTimetable] = useState<FacultyTimetable | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY);
    setFacultyId("");
    setShowErrors(false);
    setServerError(null);
    setTimetable(null);
    if (mode === "self") fetchMyTimetable().then(setTimetable).catch(() => setTimetable(null));
  }, [open, mode]);

  useEffect(() => {
    if (mode !== "admin" || facultyId === "") return;
    setTimetable(null);
    fetchFacultyTimetable(facultyId).then(setTimetable).catch(() => setTimetable(null));
  }, [mode, facultyId]);

  const year = useMemo(() => {
    const active = (academicYears ?? []).find((y) => y.status === "ACTIVE");
    const start = parseYearDate(active?.startDate);
    const end = parseYearDate(active?.endDate);
    return active && start && end ? { name: active.name, start, end } : null;
  }, [academicYears]);

  const workingDays = workingDaysOf(timetable);
  const existingForFaculty = mode === "admin" ? existing.filter((leave) => leave.facultyId === facultyId) : existing;
  const errors: LeaveFormErrors = useMemo(() => {
    const found = validateLeave(form, { year, workingDays, existing: existingForFaculty });
    if (mode === "admin" && facultyId === "") found.form = found.form ?? "Pick a faculty.";
    return found;
  }, [form, year, workingDays, existingForFaculty, mode, facultyId]);
  const shown: LeaveFormErrors = showErrors ? errors : {};

  const singleDay = Boolean(form.fromDate && form.toDate && form.fromDate.isSame(form.toDate, "day"));
  const preview =
    form.fromDate?.isValid() && form.toDate?.isValid() && !form.toDate.isBefore(form.fromDate, "day")
      ? {
          days: countDays(form.fromDate, form.toDate, form.halfDay, workingDays),
          periods: countPeriods(form.fromDate, form.toDate, form.halfDay, timetable),
        }
      : null;

  const set = (changes: Partial<LeaveForm>) => {
    setServerError(null);
    setForm((prev) => {
      const next = { ...prev, ...changes };
      // To follows From until it's picked; a range of days can't be a half day
      if (changes.fromDate && (!prev.toDate || prev.toDate.isBefore(changes.fromDate, "day"))) next.toDate = changes.fromDate;
      if (next.fromDate && next.toDate && !next.fromDate.isSame(next.toDate, "day")) next.halfDay = null;
      return next;
    });
  };

  const submit = async () => {
    setShowErrors(true);
    if (Object.keys(errors).length > 0) return;
    setSaving(true);
    setServerError(null);
    try {
      const request = {
        fromDate: form.fromDate!.format("YYYY-MM-DD"),
        toDate: form.toDate!.format("YYYY-MM-DD"),
        halfDay: form.halfDay,
        leaveType: form.leaveType as LeaveRequest["leaveType"],
        reason: form.reason.trim(),
      };
      const saved = mode === "admin" ? await applyLeaveForFaculty({ ...request, facultyId: facultyId as number }) : await applyMyLeave(request);
      onApplied(saved);
    } catch (err) {
      setServerError(leaveErrorMessage(err, "Failed to apply for leave."));
    } finally {
      setSaving(false);
    }
  };

  const dateField = (label: string, value: Dayjs | null, onChange: (value: Dayjs | null) => void, error?: string, minDate?: Dayjs) => (
    <DatePicker
      label={label}
      value={value}
      onChange={onChange}
      format="DD-MM-YYYY"
      minDate={minDate ?? year?.start}
      maxDate={year?.end}
      disabled={saving}
      slotProps={{
        textField: ({ isFieldRequired, ...params }: any) => ({
          ...params,
          size: "small",
          fullWidth: true,
          required: true,
          error: Boolean(error),
          helperText: error,
        }),
      }}
    />
  );

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
      <DialogTitle sx={{ fontWeight: 600, pb: 0.5 }}>{mode === "admin" ? "Add leave for a faculty" : "Apply for leave"}</DialogTitle>
      <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 1.5 }}>
        {mode === "admin" ? "Leave added here is approved at once." : "Your request goes to the admin for approval."}
      </Typography>

      <DialogContent dividers>
        <Stack spacing={2.25}>
          {mode === "admin" && (
            <FormControl size="small" fullWidth required error={showErrors && facultyId === ""}>
              <InputLabel id="leave-faculty">Faculty</InputLabel>
              <Select labelId="leave-faculty" label="Faculty" value={facultyId} onChange={(event) => setFacultyId(Number(event.target.value))} disabled={saving}>
                {faculties.map((faculty) => (
                  <MenuItem key={faculty.id} value={faculty.id}>
                    {faculty.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            {dateField("From", form.fromDate, (value) => set({ fromDate: value }), shown.fromDate)}
            {dateField("To", form.toDate, (value) => set({ toDate: value }), shown.toDate, form.fromDate ?? undefined)}
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: "block", mb: 0.5 }}>
              Duration
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              color="primary"
              value={form.halfDay ?? "FULL"}
              onChange={(_, value) => value && set({ halfDay: value === "FULL" ? null : (value as LeaveHalfDay) })}
              disabled={saving}
              sx={toggleSx}
            >
              <ToggleButton value="FULL">Full day{form.fromDate && !singleDay ? "s" : ""}</ToggleButton>
              <ToggleButton value="FIRST_HALF" disabled={!singleDay}>
                First half
              </ToggleButton>
              <ToggleButton value="SECOND_HALF" disabled={!singleDay}>
                Second half
              </ToggleButton>
            </ToggleButtonGroup>
            {shown.halfDay && <FormHelperText error>{shown.halfDay}</FormHelperText>}
          </Box>

          <Box>
            <Typography variant="caption" color={shown.leaveType ? "error" : "text.secondary"} fontWeight={600} sx={{ display: "block", mb: 0.5 }}>
              Leave type *
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              color="primary"
              value={form.leaveType}
              onChange={(_, value) => value && set({ leaveType: value })}
              disabled={saving}
              sx={toggleSx}
            >
              {LEAVE_TYPES.map((type) => (
                <ToggleButton key={type.value} value={type.value}>
                  {type.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {shown.leaveType && <FormHelperText error>Pick a leave type</FormHelperText>}
          </Box>

          <TextField
            label="Reason"
            required
            multiline
            minRows={3}
            fullWidth
            size="small"
            value={form.reason}
            onChange={(event) => set({ reason: event.target.value })}
            disabled={saving}
            error={Boolean(shown.reason)}
            helperText={shown.reason ?? `${form.reason.trim().length} / ${LEAVE_TEXT_MAX}`}
            slotProps={{ htmlInput: { maxLength: LEAVE_TEXT_MAX } }}
          />

          {preview && (
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
              sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: (t) => alpha(t.palette.primary.main, 0.06), color: "primary.main" }}
            >
              <EventNoteOutlinedIcon fontSize="small" />
              <Typography variant="body2" fontWeight={600}>
                {preview.days === 0 ? "No working days" : daysLabel(preview.days)}
                {timetable && ` · ${preview.periods} period${preview.periods === 1 ? "" : "s"} affected`}
              </Typography>
            </Stack>
          )}

          {(shown.form || serverError) && <Alert severity="error">{serverError ?? shown.form}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}
        >
          {saving ? "Saving…" : mode === "admin" ? "Add leave" : "Apply"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
